import React from "react";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  ListItemText,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";

import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_labor_driver_sales_filter_values,
  API_get_labor_driver_sales_metadata,
  API_preview_labor_driver_sales,
} from "../../../constants";


const modalStyle = {
  position: "absolute",
  top: "5vh",
  left: "50%",
  transform: "translateX(-50%)",
  width: "min(1120px, 94vw)",
  height: "90vh",
  bgcolor: "background.paper",
  borderRadius: "18px",
  boxShadow: 24,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const getSelectStyles = () => ({
  backgroundColor: "white",
  borderRadius: "10px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
});

const normalizeDriverForm = (driver) => {
  const salesDriver = driver?.labor_driver_sales || {};
  const rawFilterValues = Array.isArray(salesDriver.filter_values)
    ? salesDriver.filter_values
    : (salesDriver.filter_values_str || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
  const isLegacyNoFilter =
    (salesDriver.filter_column || "").toLowerCase() === "key" &&
    rawFilterValues.some((value) =>
      ["nofilter", "no filter", "all sales"].includes(value.toLowerCase())
    );

  return {
    labor_driver_sales_uuid: salesDriver.uuid || "",
    labor_driver_uuid: driver?.uuid || "",
    name: driver?.name || "",
    filter_column: isLegacyNoFilter ? "" : salesDriver.filter_column || "",
    filter_values: isLegacyNoFilter ? [] : rawFilterValues,
    target_column: salesDriver.target_column || "",
    aggregate_type: salesDriver.aggregate_type || "sum",
  };
};

const renderSelectedValues = (selected) => {
  if (!selected?.length) {
    return <span className="text-slate-400">Select values</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 py-1">
      {selected.map((value) => (
        <Chip
          key={value}
          label={value}
          size="small"
          sx={{
            backgroundColor: "#ede9fe",
            color: "#6d28d9",
            borderRadius: "8px",
          }}
        />
      ))}
    </div>
  );
};

function LaborDriverSalesBuilderModal({
  open,
  driver,
  companyId,
  onClose,
  onSave,
  onDelete,
  onPreviewCacheUpdate,
}) {
  const isEdit = Boolean(driver?.uuid);
  const [form, setForm] = React.useState(() => normalizeDriverForm(driver));
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const [metadata, setMetadata] = React.useState(null);
  const [metadataLoading, setMetadataLoading] = React.useState(false);
  const [metadataError, setMetadataError] = React.useState("");

  const [valueOptionsCache, setValueOptionsCache] = React.useState({});
  const [valueOptionsLoading, setValueOptionsLoading] = React.useState({});
  const [valueOptionsError, setValueOptionsError] = React.useState("");

  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [lastPreviewKey, setLastPreviewKey] = React.useState("");

  React.useEffect(() => {
    setMetadata(null);
    setValueOptionsCache({});
    setValueOptionsLoading({});
    setValueOptionsError("");
  }, [companyId]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setForm(normalizeDriverForm(driver));
    setSubmitAttempted(false);
    setPreviewError("");
    setPreview(null);
    setLastPreviewKey("");
    setPreviewLoading(false);
    setValueOptionsError("");
  }, [driver, open]);

  React.useEffect(() => {
    if (!open || !companyId || metadata) {
      return;
    }

    let isMounted = true;
    setMetadataLoading(true);
    setMetadataError("");

    getRightworkAPI(API_get_labor_driver_sales_metadata, { company_id: companyId })
      .then((res) => {
        if (!isMounted) {
          return;
        }
        setMetadata(res?.data?.redux?.payload || null);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        setMetadataError(
          error?.response?.data?.system?.message ||
            "Unable to load sales driver metadata."
        );
      })
      .finally(() => {
        if (isMounted) {
          setMetadataLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [companyId, metadata, open]);

  const fetchFilterValues = React.useCallback(
    async (field) => {
      if (!field || valueOptionsCache[field] || valueOptionsLoading[field]) {
        return;
      }
      setValueOptionsError("");
      setValueOptionsLoading((current) => ({ ...current, [field]: true }));
      try {
        const res = await getRightworkAPI(API_get_labor_driver_sales_filter_values, {
          company_id: companyId,
          field,
        });
        const values = res?.data?.redux?.payload?.values || [];
        setValueOptionsCache((current) => ({ ...current, [field]: values }));
      } catch (error) {
        setValueOptionsError(
          error?.response?.data?.system?.message ||
            "Unable to load filter values."
        );
      } finally {
        setValueOptionsLoading((current) => ({ ...current, [field]: false }));
      }
    },
    [companyId, valueOptionsCache, valueOptionsLoading]
  );

  React.useEffect(() => {
    if (open && form.filter_column) {
      fetchFilterValues(form.filter_column);
    }
  }, [fetchFilterValues, form.filter_column, open]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const errors = React.useMemo(() => {
    const nextErrors = {};
    if (!form.name.trim()) {
      nextErrors.name = "Name is required.";
    }
    if (!form.target_column) {
      nextErrors.target_column = "Aggregate field is required.";
    }
    if (!form.aggregate_type) {
      nextErrors.aggregate_type = "Aggregate type is required.";
    }
    if (form.filter_column && !form.filter_values.length) {
      nextErrors.filter_values = "Choose at least one filter value.";
    }
    return nextErrors;
  }, [form.aggregate_type, form.filter_column, form.filter_values.length, form.name, form.target_column]);

  const canPreview = Boolean(
    companyId &&
      form.target_column &&
      form.aggregate_type &&
      (!form.filter_column || form.filter_values.length)
  );

  const currentPreviewKey = React.useMemo(
    () =>
      JSON.stringify({
        companyId,
        filter_column: form.filter_column,
        filter_values: form.filter_values,
        target_column: form.target_column,
        aggregate_type: form.aggregate_type,
      }),
    [
      companyId,
      form.aggregate_type,
      form.filter_column,
      form.filter_values,
      form.target_column,
    ]
  );
  const previewIsStale = Boolean(preview) && lastPreviewKey !== currentPreviewKey;

  const runPreview = async () => {
    if (!canPreview) {
      setPreview(null);
      setPreviewError("");
      return;
    }

    setPreviewLoading(true);
    setPreviewError("");
    try {
      const response = await postRightworkAPI(API_preview_labor_driver_sales, {
        company_id: companyId,
        filter_column: form.filter_column,
        filter_values: form.filter_values,
        target_column: form.target_column,
        aggregate_type: form.aggregate_type,
      });
      const payload = response?.data?.redux?.payload || null;
      setPreview(payload);
      setLastPreviewKey(currentPreviewKey);
      if (payload && form.labor_driver_uuid && onPreviewCacheUpdate) {
        onPreviewCacheUpdate(form.labor_driver_uuid, payload);
      }
    } catch (error) {
      setPreview(null);
      setPreviewError(
        error?.response?.data?.system?.message ||
          "Unable to preview this sales group."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        labor_driver_sales_uuid: form.labor_driver_sales_uuid,
        labor_driver_uuid: form.labor_driver_uuid,
        name: form.name.trim(),
        filter_column: form.filter_column,
        filter_values: form.filter_values,
        target_column: form.target_column,
        aggregate_type: form.aggregate_type,
      });
      onClose();
    } catch (error) {
      toast.error(
        error?.message ||
          error?.response?.data?.system?.message ||
          "Unable to save sales group."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!driver?.uuid) {
      return;
    }
    setIsSaving(true);
    try {
      await onDelete(driver.uuid);
      onClose();
    } catch (error) {
      toast.error(
        error?.message ||
          error?.response?.data?.system?.message ||
          "Unable to delete sales group."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const aggregateFields = metadata?.aggregate_fields || [];
  const aggregateField = aggregateFields.find(
    (item) => item.key === form.target_column
  );
  const aggregateTypes = metadata?.aggregate_types || [];
  const supportedAggregateTypes =
    aggregateField?.supported_aggregations?.length
      ? aggregateTypes.filter((item) =>
          aggregateField.supported_aggregations.includes(item.key)
        )
      : aggregateTypes;
  const filterAttributes = metadata?.filter_attributes || [];
  const currentValueOptions = valueOptionsCache[form.filter_column] || [];
  const selectedAggregateLabel =
    aggregateTypes.find((item) => item.key === form.aggregate_type)?.label ||
    "Aggregate";

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              {isEdit ? "Edit Sales Group" : "Create Sales Group"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Build a labor sales metric with guided filters and a live 30-day preview.
            </p>
          </div>
          <IconButton onClick={handleClose} disabled={isSaving}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="grid min-h-full grid-cols-1 lg:grid-cols-[1.15fr,0.85fr] divide-y divide-slate-200 lg:divide-x lg:divide-y-0">
          <div className="px-6 py-6">
            {metadataError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {metadataError}
              </Alert>
            )}

            <div className="space-y-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Name
                </label>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Lunch Entrees Sales"
                  value={form.name}
                  onChange={(event) => updateForm("name", event.target.value)}
                  error={submitAttempted && Boolean(errors.name)}
                  helperText={submitAttempted ? errors.name : ""}
                  InputProps={{ sx: getSelectStyles() }}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Filter Attribute
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={form.filter_column}
                    onChange={(event) => {
                      const nextField = event.target.value;
                      updateForm("filter_column", nextField);
                      updateForm("filter_values", []);
                      if (nextField) {
                        fetchFilterValues(nextField);
                      }
                    }}
                    displayEmpty
                    sx={getSelectStyles()}
                    disabled={metadataLoading}
                  >
                    <MenuItem value="">
                      <em>No filter / all sales</em>
                    </MenuItem>
                    {filterAttributes.map((attribute) => (
                      <MenuItem key={attribute.key} value={attribute.key}>
                        {attribute.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Filter Values
                </label>
                <FormControl
                  fullWidth
                  size="small"
                  error={submitAttempted && Boolean(errors.filter_values)}
                >
                  {valueOptionsLoading[form.filter_column] ? (
                    <div className="flex h-11 items-center justify-center rounded-lg border border-slate-300 bg-white">
                      <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
                    </div>
                  ) : (
                    <Select
                      multiple
                      value={form.filter_values}
                      onChange={(event) =>
                        updateForm("filter_values", event.target.value)
                      }
                      disabled={!form.filter_column || metadataLoading}
                      displayEmpty
                      input={<OutlinedInput />}
                      renderValue={renderSelectedValues}
                      sx={getSelectStyles()}
                    >
                      {currentValueOptions.map((value) => (
                        <MenuItem key={value} value={value}>
                          <Checkbox
                            checked={form.filter_values.includes(value)}
                            sx={{
                              "&.Mui-checked": {
                                color: "#6d28d9",
                              },
                            }}
                          />
                          <ListItemText primary={value} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  <FormHelperText>
                    {submitAttempted && errors.filter_values
                      ? errors.filter_values
                      : valueOptionsError ||
                        (form.filter_column
                          ? "Select one or more values for this sales group."
                          : "Leave blank to include all sales." )}
                  </FormHelperText>
                </FormControl>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr,220px]">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Aggregate Field
                  </label>
                  <FormControl
                    fullWidth
                    size="small"
                    error={submitAttempted && Boolean(errors.target_column)}
                  >
                    <Select
                      value={form.target_column}
                      onChange={(event) => {
                        const nextTarget = event.target.value;
                        const fieldConfig = aggregateFields.find(
                          (item) => item.key === nextTarget
                        );
                        const supportedTypes =
                          fieldConfig?.supported_aggregations || [];
                        updateForm("target_column", nextTarget);
                        if (
                          supportedTypes.length &&
                          !supportedTypes.includes(form.aggregate_type)
                        ) {
                          updateForm("aggregate_type", supportedTypes[0]);
                        }
                      }}
                      displayEmpty
                      sx={getSelectStyles()}
                      disabled={metadataLoading}
                    >
                      <MenuItem value="" disabled>
                        <em>Select field</em>
                      </MenuItem>
                      {aggregateFields.map((field) => (
                        <MenuItem key={field.key} value={field.key}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {submitAttempted ? errors.target_column : "Choose the value to aggregate."}
                    </FormHelperText>
                  </FormControl>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Aggregate Type
                  </label>
                  <FormControl
                    fullWidth
                    size="small"
                    error={submitAttempted && Boolean(errors.aggregate_type)}
                  >
                    <Select
                      value={form.aggregate_type}
                      onChange={(event) =>
                        updateForm("aggregate_type", event.target.value)
                      }
                      sx={getSelectStyles()}
                      disabled={metadataLoading || !form.target_column}
                    >
                      {supportedAggregateTypes.map((item) => (
                        <MenuItem key={item.key} value={item.key}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {submitAttempted ? errors.aggregate_type : "Choose how to aggregate matching rows."}
                    </FormHelperText>
                  </FormControl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 px-6 py-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Preview</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Recent 30-day estimate for this sales group.
                  </p>
                </div>
                <LoadingButton
                  variant="outlined"
                  loading={previewLoading}
                  onClick={runPreview}
                  disabled={!canPreview}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    borderColor: "#c4b5fd",
                    color: "#6d28d9",
                    "&:hover": {
                      borderColor: "#8b5cf6",
                      backgroundColor: "#f5f3ff",
                    },
                  }}
                >
                  {preview ? "Recalculate Preview" : "Calculate Preview"}
                </LoadingButton>
              </div>

              {!canPreview && (
                <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  Select an aggregate field and, if filtering, choose at least one filter value to preview the group size.
                </div>
              )}

              {canPreview && !previewLoading && !preview && !previewError && (
                <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  Click <span className="font-medium text-slate-700">Calculate Preview</span> to estimate the recent size of this sales group.
                </div>
              )}

              {canPreview && previewError && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {previewError}
                </Alert>
              )}

              {canPreview && !previewError && preview && (
                <>
                  {previewIsStale && (
                    <Alert severity="info" sx={{ mt: 3 }}>
                      The preview below is out of date. Recalculate it to reflect your latest changes.
                    </Alert>
                  )}
                  <div className="mt-4 rounded-xl bg-violet-50 px-4 py-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-violet-700">
                      {selectedAggregateLabel}
                    </div>
                    <div className="mt-1 text-3xl font-semibold text-slate-900">
                      {Number(preview.total_value || 0).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div className="mt-1 text-sm text-slate-500">
                      {preview.start_date} to {preview.end_date}
                    </div>
                  </div>

                  {preview.is_empty ? (
                    <Alert severity="warning" sx={{ mt: 3 }}>
                      No matching sales rows were found in the preview window.
                    </Alert>
                  ) : (
                    <>
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div className="rounded-xl border border-slate-200 px-4 py-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Matching Stores
                          </div>
                          <div className="mt-1 text-xl font-semibold text-slate-900">
                            {preview.stores_with_matches} / {preview.stores_considered}
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 px-4 py-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Rows Matched
                          </div>
                          <div className="mt-1 text-xl font-semibold text-slate-900">
                            {Number(preview.rows_matched || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 px-4 py-3 col-span-2">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Avg per Store / Day
                          </div>
                          <div className="mt-1 text-xl font-semibold text-slate-900">
                            {Number(preview.average_per_store_per_day || 0).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })}
                          </div>
                        </div>
                      </div>

                      {preview.top_filter_values?.length > 0 && (
                        <div className="mt-5">
                          <h4 className="text-sm font-medium text-slate-700">
                            Top Matching Values
                          </h4>
                          <div className="mt-3 space-y-2">
                            {preview.top_filter_values.map((item) => (
                              <div
                                key={item.value}
                                className="rounded-xl border border-slate-200 px-4 py-3"
                              >
                                <div className="text-sm font-medium text-slate-900">
                                  {item.value}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                  {Number(item.rows_matched || 0).toLocaleString()} rows
                                  {" · "}
                                  {Number(item.total_value || 0).toLocaleString(undefined, {
                                    maximumFractionDigits: 2,
                                  })}{" "}
                                  total
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">
          <div>
            {isEdit && (
              <button
                className="inline-flex items-center rounded-lg border border-rose-200 px-4 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleDelete}
                disabled={isSaving}
                type="button"
              >
                <DeleteOutlineIcon sx={{ fontSize: 18, mr: 1 }} />
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleClose}
              disabled={isSaving}
              type="button"
            >
              Cancel
            </button>
            <LoadingButton
              variant="contained"
              loading={isSaving}
              onClick={handleSave}
              endIcon={<SaveIcon />}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                paddingX: "18px",
                paddingY: "10px",
                backgroundColor: "#6d28d9",
                "&:hover": {
                  backgroundColor: "#5b21b6",
                },
              }}
            >
              {isEdit ? "Save Sales Group" : "Create Sales Group"}
            </LoadingButton>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default LaborDriverSalesBuilderModal;
